import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { graphierQL } from 'utils'
import {
  GetWorkspaceByIdQuery,
  GetWorkspaceByIdQueryVariables
} from '~workspace/types/__generated/gql/graphql'

export const getWorkspaceByIdQuery = /* GraphQL */ `
  query GetWorkspaceById($id: ID!) {
    getWorkspaceById(id: $id) {
      id
      name
      description
      inviteId
      createdAt
      logo
      email
      twitterUsername
      websiteUrl
      communityUrl
      defaultUser {
        id
        firstName
        lastName
      }
      memberCollection {
        total
        items {
          id
          role
          createdAt
          user {
            id
            uid
            firstName
            lastName
            avatar
            email
            createdAt
          }
        }
      }
      guestCollection {
        total
        items {
          id
          role
          createdAt
          user {
            id
            uid
            firstName
            lastName
            avatar
            email
            createdAt
          }
          mapGuestAccessCollection {
            total
            items {
              id
              createdAt
              map {
                id
                createdAt
                name
              }
            }
          }
        }
      }
      invitedUserCollection {
        total
        items {
          id
          workspaceId
          userId
          email
          role
          createdAt
          mapGuestAccessCollection {
            total
            items {
              id
              createdAt
              map {
                id
                createdAt
                name
              }
            }
          }
        }
      }
      tagCollection {
        items {
          id
          type
          name
          count
        }
        total
      }
    }
  }
`

export type GetWorkspaceByIdVariables = GetWorkspaceByIdQueryVariables
export type GetWorkspaceByIdResults = GetWorkspaceByIdQuery['getWorkspaceById']

export const getWorkspaceByIdQueryKey = (id: string) => ['getWorkspaceById', id]

export const getWorkspaceById = (workspaceId: string) =>
  graphierQL<GetWorkspaceByIdQuery, GetWorkspaceByIdVariables>({
    query: getWorkspaceByIdQuery,
    variables: { id: workspaceId }
  }).then((res) => res.getWorkspaceById)

export const useGetWorkspaceById = (
  workspaceId: string
): UseQueryResult<GetWorkspaceByIdResults> => {
  return useQuery({
    queryKey: getWorkspaceByIdQueryKey(workspaceId),
    queryFn: () => getWorkspaceById(workspaceId)
  })
}

export const useGetCurrentWorkspace =
  (): UseQueryResult<GetWorkspaceByIdResults> => {
    const router = useRouter()
    const workspaceId = router.query.workspaceId as string
    return useGetWorkspaceById(workspaceId)
  }

export const useGetCurrentWorkspaceMembers = () => {
  const { data } = useGetCurrentWorkspace()
  return data ? data.memberCollection.items : []
}
export const useGetCurrentWorkspaceGuests = () => {
  const { data } = useGetCurrentWorkspace()
  return data ? data.guestCollection.items : []
}

export const useGetCurrentWorkspaceInvitedUsers = () => {
  const { data } = useGetCurrentWorkspace()
  return data ? data.invitedUserCollection.items : []
}
